// Profil.
//   profile_identity  hero + card      initials, name, role badge and organization;
//                                      the account facts in the Data akun card
//   role_mode         card             Google accounts with a Non-Penyedia role only:
//                                      the same Mode control as the sidebar foot
//                                      (shell/RoleMode.tsx, variant "field"; design
//                                      review ui.region_gate profil_role_region, 0.80)
//   penyedia_type     card             Penyedia only: Bentuk usaha (Badan Usaha or
//                                      Perorangan, PATCH /me/penyedia-type)
//   google_drive      card             Google accounts only: the Drive connection
//                                      (GET /auth/google/connection), folder Pluto,
//                                      Hubungkan ulang and Putuskan
//   password_form     card             change password (password accounts only)
//   session_actions   card row         Keluar
// A password account's role is read-only ("Peran diatur administrator"):
// the administrator sets it.
import { useState, type FormEvent } from "react";
import * as API from "../api";
import type { User } from "../api";
import { useAuth, useUser } from "../auth";
import { formatDateTime } from "../lib/format";
import { isNonPenyedia, ROLE_LABEL } from "../lib/labels";
import { isGoogleAccount } from "../lib/account";
import { googleNav } from "../shell/GoogleSignIn";
import { Dialog, Field, fieldAria, Notice, PasswordInput, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";
import { canSwitchRole, RoleModeMenu } from "../shell/RoleMode";
import { ChoiceRows } from "./choice";
import { PENYEDIA_TYPES } from "./Onboarding";

export function ProfileScreen() {
  useTitle("Profil");
  const user = useUser();
  const { signOut } = useAuth();
  const [leaving, setLeaving] = useState(false);
  const facts: [string, string][] = [
    ["Email", user.email],
    ["Peran", ROLE_LABEL[user.role]],
    ...(user.role === "penyedia" ? [["Bentuk usaha", penyediaTypeLabel(user.penyediaType)] as [string, string]] : []),
    // A Penyedia is a business: its company, never a civil servant's NIP.
    [user.role === "penyedia" ? "Nama usaha" : "Organisasi", user.organization ?? "Belum diisi"],
    ...(user.role === "penyedia" ? [] : [["NIP", user.nip ?? "Belum diisi"] as [string, string]]),
    ["Telepon", user.phone ?? "Belum diisi"],
    ["Login terakhir", formatDateTime(user.lastLoginAt)],
  ];
  return (
    <div className="pf">
      {/* Who is signed in: initials, name, role and organization. */}
      <header className="page-head pf-hero">
        <span className="pf-avatar" aria-hidden="true">
          {initials(user.name)}
        </span>
        <div className="pf-hero-text">
          <h1 className="page-title">{user.name}</h1>
          <p className="pf-hero-meta">
            <span className="badge" data-tone="info">
              {ROLE_LABEL[user.role]}
            </span>
            {user.organization ? <span>{user.organization}</span> : null}
          </p>
        </div>
      </header>
      <section className="card pf-card" aria-labelledby="pf-facts-title">
        <h2 id="pf-facts-title" className="card-title">
          Data akun
        </h2>
        <dl className="facts pf-facts">
          {facts.map(([k, v]) => (
            <div key={k} className="fact">
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
        <p className="quiet-line pf-note">
          <Glyph name="infoCircle" size={16} />
          {user.role === "admin" ? (
            <span>
              Ubah data akun, termasuk akun Anda sendiri, di menu <a href="/admin/akun">Akun</a>.
            </span>
          ) : (
            <span>{roleNote(user)}</span>
          )}
        </p>
      </section>
      {/* A Penyedia changes its mode in the Bentuk usaha card below (or the sidebar Mode menu). */}
      {canSwitchRole(user) && user.role !== "penyedia" ? (
        <section className="card form-card pf-card" aria-labelledby="role-title">
          <h2 id="role-title" className="card-title">
            Mode peran
          </h2>
          <p id="role-lead" className="section-lead">
            Pilih peran yang sedang Anda jalankan. Menu dan jenis dokumen menyesuaikan peran ini.
          </p>
          <RoleModeMenu user={user} variant="field" labelledBy="role-lead" />
        </section>
      ) : null}
      {user.role === "penyedia" ? <PenyediaTypeCard /> : null}
      {user.authProvider === "google" ? <DriveCard /> : null}
      {user.authProvider === "password" ? <PasswordCard /> : null}
      <section className="card pf-card pf-session" aria-labelledby="session-title">
        <div className="pf-session-text">
          <h2 id="session-title" className="card-title">
            Keluar
          </h2>
          <p className="section-lead">{user.role === "admin" ? "Sesi di perangkat ini diakhiri dan Anda kembali ke halaman masuk." : "Sesi di perangkat ini diakhiri dan Anda kembali ke chat sebagai tamu."}</p>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          aria-busy={leaving || undefined}
          onClick={async () => {
            setLeaving(true);
            await signOut();
          }}
        >
          <Glyph name="logout" size={16} />
          <span>Keluar</span>
        </button>
      </section>
    </div>
  );
}

/** Up to two initials of a name, for the avatar ("Dewi Lestari" is "DL"). */
export function initials(name: string): string {
  const words = name.replace(/^(Ir|Dr|Drs|Prof|H)\.\s+/i, "").trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
}

export function penyediaTypeLabel(type: API.PenyediaType | null | undefined): string {
  return PENYEDIA_TYPES.find((t) => t.id === type)?.label ?? "Belum dipilih";
}

/** Bentuk usaha of a Penyedia: chosen at onboarding, changed here. */
function PenyediaTypeCard() {
  const user = useUser();
  const { setUser } = useAuth();
  const [value, setValue] = useState<API.PenyediaType | null>(user.penyediaType ?? null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const changed = value !== (user.penyediaType ?? null);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!changed || busy) return;
    setBusy(true);
    setFailure(null);
    setDone(false);
    try {
      setUser(await API.setPenyediaType(value));
      setDone(true);
    } catch (err) {
      setFailure(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card form-card pf-card" aria-labelledby="ptype-title">
      <h2 id="ptype-title" className="card-title">
        Bentuk usaha
      </h2>
      <p className="section-lead">Mode Penyedia Anda: Badan Usaha menandatangani dokumen atas nama usaha, Perorangan atas nama Anda sendiri. Pluto juga menyesuaikan sarannya. Mode ini bisa diganti dari menu Mode di samping.</p>
      {done ? (
        <Notice tone="success" title="Bentuk usaha disimpan">
          <p>{penyediaTypeLabel(user.penyediaType)}.</p>
        </Notice>
      ) : null}
      {failure ? (
        <Notice tone="danger" title="Bentuk usaha belum disimpan">
          <p>{failure}</p>
        </Notice>
      ) : null}
      <form className="form-stack" noValidate onSubmit={save} aria-label="Bentuk usaha">
        <ChoiceRows labelledBy="ptype-title" idBase="ptype" options={PENYEDIA_TYPES} value={value} onChange={(next) => { setValue(next); setDone(false); }} />
        <div className="button-row">
          <button type="submit" className="btn" disabled={!changed} aria-busy={busy || undefined}>
            Simpan bentuk usaha
          </button>
        </div>
      </form>
    </section>
  );
}

/** Who sets this account's role, in one line under the account facts. */
export function roleNote(user: User): string {
  // Every Penyedia changes its bentuk usaha itself; only the account kind needs the admin.
  if (user.role === "penyedia") {
    return "Akun Penyedia tidak dapat pindah ke Non-Penyedia tanpa bantuan Administrator. Bentuk usaha tetap dapat diubah kapan saja di bawah atau dari menu Mode di samping.";
  }
  if (isGoogleAccount(user) && isNonPenyedia(user.role)) {
    return "Ganti peran dari Mode peran di bawah, atau dari menu samping di atas nama Anda. Jenis akun (Non-Penyedia) tidak dapat diubah tanpa bantuan Administrator.";
  }
  return "Peran diatur administrator. Perubahan data akun juga dilakukan oleh Administrator.";
}

/** The one status line of the Drive connection. */
export function driveStatusText(conn: API.DriveConnection): string {
  if (conn.status === "ok") return conn.email ? `Terhubung sebagai ${conn.email}` : "Terhubung";
  if (conn.status === "missing_scope") return "Izin Drive belum diberikan";
  if (conn.status === "revoked") return "Terputus";
  return "Belum terhubung";
}

function DriveCard() {
  const conn = useAsync(() => API.getDriveConnection(), []);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const c = conn.data;

  const reconnect = () => googleNav.assign(API.googleStartUrl({ reconnect: true, next: "/profil" }));
  const disconnect = async () => {
    setBusy(true);
    setFailure(null);
    try {
      conn.set(await API.disconnectDrive());
      setConfirm(false);
    } catch (err) {
      setFailure(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card form-card pf-card" aria-labelledby="drive-title">
      <h2 id="drive-title" className="card-title">
        Google Drive
      </h2>
      <p className="section-lead">Lampiran chat dan file yang Anda buat disimpan di folder Pluto pada Google Drive Anda.</p>
      {conn.loading && !c ? <p className="quiet-line">Memuat status Google Drive</p> : null}
      {conn.error ? (
        <Notice tone="danger" title="Status Google Drive belum dapat dimuat">
          <p>{API.errorMessage(conn.error)}</p>
        </Notice>
      ) : null}
      {failure ? (
        <Notice tone="danger" title="Google Drive belum diputuskan">
          <p>{failure}</p>
        </Notice>
      ) : null}
      {c ? (
        <>
          <dl className="facts">
            <div className="fact">
              <dt>Status</dt>
              <dd data-drive-status={c.status}>{driveStatusText(c)}</dd>
            </div>
            <div className="fact">
              <dt>Folder Pluto</dt>
              <dd>
                {c.plutoFolderUrl ? (
                  <a href={c.plutoFolderUrl} target="_blank" rel="noopener noreferrer">
                    Buka di Drive
                  </a>
                ) : (
                  "Belum ada"
                )}
              </dd>
            </div>
            {c.connectedAt ? (
              <div className="fact">
                <dt>Terhubung sejak</dt>
                <dd>{formatDateTime(c.connectedAt)}</dd>
              </div>
            ) : null}
          </dl>
          <div className="button-row">
            <button type="button" className={c.status === "ok" ? "btn btn-secondary" : "btn"} onClick={reconnect}>
              Hubungkan ulang
            </button>
            {c.status !== "none" ? (
              <button type="button" className="btn btn-secondary" onClick={() => setConfirm(true)}>
                Putuskan
              </button>
            ) : null}
          </div>
        </>
      ) : null}
      <Dialog
        open={confirm}
        size="sm"
        title="Putuskan Google Drive?"
        description="Pluto tidak lagi dapat menyimpan lampiran ke Drive Anda sampai Anda menghubungkan ulang. File yang sudah ada tetap di Drive Anda."
        busy={busy}
        onClose={() => setConfirm(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setConfirm(false)}>
              Batal
            </button>
            <button type="button" className="btn" data-tone="danger" aria-busy={busy || undefined} onClick={disconnect}>
              Ya, putuskan
            </button>
          </>
        }
      >
        <span />
      </Dialog>
    </section>
  );
}

export function validatePassword(current: string, next: string, confirm: string): Record<string, string> {
  const e: Record<string, string> = {};
  if (!current) e.current = "Kata sandi saat ini wajib diisi.";
  if (!next) e.next = "Kata sandi baru wajib diisi.";
  else if (next.length < 8) e.next = "Minimal 8 karakter.";
  else if (next === current) e.next = "Gunakan kata sandi yang berbeda dari sebelumnya.";
  if (next && confirm !== next) e.confirm = "Belum sama dengan kata sandi baru.";
  return e;
}

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const found = validatePassword(current, next, confirm);
    setErrors(found);
    setFailure(null);
    setDone(false);
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      await API.changePassword(current, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      setDone(true);
    } catch (err) {
      if (err instanceof API.ApiError && (err.status === 400 || err.status === 401 || err.status === 422)) {
        setErrors({ current: err.status === 400 ? err.message : "Kata sandi saat ini tidak sesuai." });
      } else setFailure(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card form-card pf-card" aria-labelledby="pw-title">
      <h2 id="pw-title" className="card-title">
        Ubah kata sandi
      </h2>
      {done ? (
        <Notice tone="success" title="Kata sandi diperbarui">
          <p>Gunakan kata sandi baru saat masuk berikutnya.</p>
        </Notice>
      ) : null}
      {failure ? (
        <Notice tone="danger" title="Kata sandi belum diperbarui">
          <p>{failure}</p>
        </Notice>
      ) : null}
      <form className="form-stack" noValidate onSubmit={submit}>
        <div className="field-row" data-cols="1">
          <Field id="pw-current" label="Kata sandi saat ini" required error={errors.current}>
            <PasswordInput {...fieldAria("pw-current", errors.current)} autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.currentTarget.value)} />
          </Field>
        </div>
        <div className="field-row">
          <Field id="pw-next" label="Kata sandi baru" required error={errors.next} hint="Minimal 8 karakter.">
            <PasswordInput {...fieldAria("pw-next", errors.next)} autoComplete="new-password" value={next} onChange={(e) => setNext(e.currentTarget.value)} />
          </Field>
          <Field id="pw-confirm" label="Ulangi kata sandi baru" required error={errors.confirm}>
            <PasswordInput {...fieldAria("pw-confirm", errors.confirm)} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.currentTarget.value)} />
          </Field>
        </div>
        <div className="button-row">
          <button type="submit" className="btn btn-sm" aria-busy={busy || undefined}>
            Simpan kata sandi
          </button>
        </div>
      </form>
    </section>
  );
}
